import { Controller } from "@hotwired/stimulus";

// Shows the Nexus discount-rule/discount-billings fields on the downsell form only when the selected
// replication group is Nexus, and swaps the discount-rule <select>'s options to match. No AJAX: the
// options for every Nexus replication group on this location group are embedded in the page at render
// time (see Admin::DownsellPlansHelper#nexus_discount_rule_select_data), so this is a pure client-side
// option swap — rules created in Nexus after the page loaded need a refresh to appear.
//
// Attached to the same <form> as retention-discount-switch (a separate controller, not reused here,
// because "is Nexus" is narrower than that controller's "applies retention discount in POS", which also
// covers Sonny's). See that controller's header comment for why both an on-connect and an on-change path
// are needed — single-option replication-group selects render as a hidden input that never fires change.
export default class extends Controller {
  static targets = ["fieldset"];
  static values = {
    optionsByRgId: Object,
    nexusRgIds: Array,
    initialRgId: String,
    initialRuleId: String,
  };

  connect() {
    // On load, the server-rendered <select> only ever has the blank option (the dashboard field's
    // collection is empty until this controller populates it), so its `.value` is never the plan's real
    // configured rule — restore from initialRuleIdValue (the plan's actual pos_discount_rule_id) instead.
    this.applyReplicationGroup(this.initialRgIdValue, this.initialRuleIdValue);
  }

  onReplicationGroupChange(event) {
    const { name, value } = event.target;
    if (!name || !name.endsWith("[replication_group_id]")) return;

    this.applyReplicationGroup(value, this.currentRuleId());
  }

  applyReplicationGroup(rgId, previousValue) {
    const isNexus = this.nexusRgIdsValue.map(String).includes(String(rgId));

    this.fieldsetTargets.forEach((fieldset) => {
      fieldset.disabled = !isNexus;
      // Inline style, not a "hidden" class — the fieldset already carries Tailwind's `contents` class
      // (display: contents), and a second display-affecting class would race it on specificity.
      fieldset.style.display = isNexus ? "" : "none";
    });

    const select = this.element.querySelector('select[name$="[pos_discount_rule_id]"]');
    if (!select) return;

    const options = isNexus ? this.optionsByRgIdValue[rgId] || [] : [];

    select.innerHTML = "";
    select.appendChild(new Option("", ""));
    options.forEach(([label, value]) => select.appendChild(new Option(label, value)));

    // The previous group's rule id is never valid for the newly selected group, except when re-applying
    // the same group's options unchanged (e.g. on initial connect) — only keep it if it's still present.
    select.value = options.some(([, value]) => value === previousValue) ? previousValue : "";
  }

  currentRuleId() {
    return this.element.querySelector('select[name$="[pos_discount_rule_id]"]')?.value ?? "";
  }
}
